import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { firestore, functions } from "../firebase";
import useAuth from "../hooks/useAuth";
import TopBar from "./TopBar";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { generateQuotePdfClient } from "../utils/pdf";
import { openOrDownloadPdf } from "../utils/download";
import { formatPhoneNumber, formatINR } from "../utils/format";
import { calculateAdjustedPricing, getPaymentLinkAmount } from "../utils/priceAdjustment";
import { FiFileText, FiDollarSign, FiLink, FiCopy, FiCheck, FiX, FiClock } from "react-icons/fi";
import "../styles/quotation-system.css";

export default function SalesQuotes() {
  React.useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "robots";
    metaTag.content = "noindex";
    document.head.appendChild(metaTag);
    document.body.classList.add('quotation-page');
    return () => {
      document.head.removeChild(metaTag);
      document.body.classList.remove('quotation-page');
    };
  }, []);

  const history = useHistory();
  const { user, profile } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [priceAdjustmentModal, setPriceAdjustmentModal] = useState({ open: false, quote: null });
  const [paymentLinkModal, setPaymentLinkModal] = useState({ open: false, quote: null });
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("add"); // "add" or "subtract"
  const [allowPartialPayment, setAllowPartialPayment] = useState(false);
  const [minimumPartialAmount, setMinimumPartialAmount] = useState("");
  const [linkExpiryDate, setLinkExpiryDate] = useState("");
  const [linkExpiryTime, setLinkExpiryTime] = useState("");
  const [useDefaultExpiry, setUseDefaultExpiry] = useState(true);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { modal, showAlert, showConfirm, hideModal } = useModal();

  useEffect(() => {
    if (!user) return;
    
    // Load quotes
    const unsubQuotes = firestore
      .collection("quotes")
      .where("meta.salespersonUid", "==", user.uid)
      .orderBy("meta.createdAt", "desc")
      .limit(100)
      .onSnapshot((snap) => {
        setQuotes(snap.docs.map((d) => d.data()));
      });

    // Load pending approvals (only for sales users)
    let unsubApprovals = null;
    if (profile?.role === "sales") {
      unsubApprovals = firestore
        .collection("priceAdjustmentRequests")
        .where("salespersonUid", "==", user.uid)
        .where("status", "==", "pending")
        .orderBy("requestedAt", "desc")
        .onSnapshot((snap) => {
          setPendingApprovals(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
    }

    return () => {
      unsubQuotes();
      if (unsubApprovals) unsubApprovals();
    };
  }, [user, profile]);

  const openQuotePdf = async (q) => {
    try {
      const { blob, filename } = await generateQuotePdfClient({ quote: q });
      // On desktop: open in new tab, on mobile: download
      openOrDownloadPdf(blob, filename);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      showAlert("Failed to generate PDF. Please try again.", "error", "Error");
    }
  };

  const handlePriceAdjustment = (quote) => {
    setPriceAdjustmentModal({ open: true, quote });
    setAdjustmentAmount("");
    setAdjustmentType("add");
  };

  const handleSubmitPriceAdjustment = async () => {
    if (!priceAdjustmentModal.quote) return;
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount <= 0) {
      showAlert("Please enter a valid positive amount.", "warning", "Invalid Amount");
      return;
    }

    const finalAdjustment = adjustmentType === "subtract" ? -amount : amount;
    const quote = priceAdjustmentModal.quote;

    // Check if adjustment would make base price negative
    if (quote.derived.basePrice + finalAdjustment < 0) {
      showAlert("Adjustment amount is too large. Base price cannot be negative.", "warning", "Invalid Adjustment");
      return;
    }

    setProcessing(true);
    try {
      // If user is admin, apply directly
      if (profile?.role === "admin") {
        const adjustedDerived = calculateAdjustedPricing(quote.derived, finalAdjustment);
        await firestore.collection("quotes").doc(quote.meta.id).update({
          derived: adjustedDerived,
          "meta.priceAdjustedAt": Date.now(),
          "meta.priceAdjustedBy": user.uid,
          "meta.lastUpdatedAt": Date.now()
        });
        showAlert("Price adjustment applied successfully.", "success", "Success");
        setPriceAdjustmentModal({ open: false, quote: null });
      } else {
        // If user is sales, create approval request
        await firestore.collection("priceAdjustmentRequests").add({
          quoteId: quote.meta.id,
          salespersonUid: user.uid,
          salespersonName: profile?.name || user.email,
          adjustmentAmount: finalAdjustment,
          originalDerived: quote.derived,
          requestedAt: Date.now(),
          status: "pending",
          customerName: quote.customer.name,
          customerPhone: quote.customer.phone,
          quoteNo: quote.meta.quoteNo,
          capacityKw: quote.derived.capacityKw
        });
        showAlert("Price adjustment request submitted. Waiting for admin approval.", "success", "Request Submitted");
        setPriceAdjustmentModal({ open: false, quote: null });
      }
    } catch (error) {
      console.error("Error submitting price adjustment:", error);
      showAlert("Failed to submit price adjustment. Please try again.", "error", "Error");
    } finally {
      setProcessing(false);
    }
  };

  const handleGeneratePaymentLink = (quote) => {
    setPaymentLinkModal({ open: true, quote });
    setAllowPartialPayment(false);
    setMinimumPartialAmount("");
    setLinkExpiryDate("");
    setLinkExpiryTime("");
    setUseDefaultExpiry(true);
    setGeneratedLink(null);
    setLinkCopied(false);
  };

  const handleCreatePaymentLink = async () => {
    if (!paymentLinkModal.quote) return;
    const quote = paymentLinkModal.quote;
    
    setProcessing(true);
    try {
      // Get the final amount (before subsidy)
      const amount = getPaymentLinkAmount(quote.derived);
      if (amount <= 0) {
        showAlert("Invalid amount for payment link.", "warning", "Invalid Amount");
        setProcessing(false);
        return;
      }

      // Validate minimum partial amount if partial payment is enabled
      let minPartialAmount = null;
      if (allowPartialPayment) {
        if (!minimumPartialAmount || parseFloat(minimumPartialAmount) <= 0) {
          showAlert("Please enter a valid minimum partial amount (greater than 0).", "warning", "Invalid Amount");
          setProcessing(false);
          return;
        }
        minPartialAmount = parseFloat(minimumPartialAmount);
        if (minPartialAmount > amount) {
          showAlert("Minimum partial amount cannot be greater than total amount.", "warning", "Invalid Amount");
          setProcessing(false);
          return;
        }
      }

      // Prepare expiry date and time
      let linkExpiryTimeISO = null;
      if (!useDefaultExpiry && linkExpiryDate && linkExpiryTime) {
        // Combine date and time into ISO string
        // Date input gives YYYY-MM-DD, time input gives HH:MM
        const expiryDateTime = new Date(`${linkExpiryDate}T${linkExpiryTime}`);
        if (isNaN(expiryDateTime.getTime())) {
          showAlert("Please enter a valid expiry date and time.", "warning", "Invalid Expiry");
          setProcessing(false);
          return;
        }
        // Check if expiry is in the future
        if (expiryDateTime <= new Date()) {
          showAlert("Expiry date and time must be in the future.", "warning", "Invalid Expiry");
          setProcessing(false);
          return;
        }
        linkExpiryTimeISO = expiryDateTime.toISOString();
      }

      // Create payment link via Firebase function
      const createPaymentLink = functions.httpsCallable('createPaymentLink');
      const result = await createPaymentLink({
        amount: amount,
        customerName: quote.customer.name,
        customerEmail: quote.customer.email || quote.customer.phone + "@lunarelectric.in", // Fallback email
        customerPhone: quote.customer.phone,
        paymentReason: `Solar system installation - ${quote.derived.capacityKw}kW`,
        allowPartialPayment: allowPartialPayment,
        minimumPartialAmount: minPartialAmount,
        linkExpiryTime: linkExpiryTimeISO,
        linkId: null
      });

      if (result.data.success) {
        setGeneratedLink(result.data.linkUrl);
        showAlert("Payment link created successfully!", "success", "Success");
      } else {
        throw new Error(result.data.error || "Failed to create payment link");
      }
    } catch (error) {
      console.error("Error creating payment link:", error);
      showAlert(error.message || "Failed to create payment link. Please try again.", "error", "Error");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      showAlert("Failed to copy link. Please copy manually.", "warning", "Copy Failed");
    }
  };

  const approvedQuotes = useMemo(() => {
    return quotes.filter(q => !q.meta?.priceAdjustmentPending);
  }, [quotes]);

  return (
    <div className="quotation-system">
      <TopBar />
      <Modal {...modal} onClose={hideModal} />
      
      {/* Pending Approvals Section (for sales users) */}
      {profile?.role === "sales" && pendingApprovals.length > 0 && (
        <div className="elegant-container fade-in-up" style={{ marginBottom: "2rem" }}>
          <div className="elegant-card" style={{ border: "2px solid var(--accent-gold)", background: "linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)" }}>
            <h2 className="elegant-title" style={{ fontSize: "1.5rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <FiClock style={{ color: "var(--accent-gold)" }} />
              Pending Price Adjustment Approvals ({pendingApprovals.length})
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {pendingApprovals.map((approval) => (
                <div key={approval.id} style={{ 
                  padding: "1rem", 
                  background: "white", 
                  borderRadius: "8px", 
                  border: "1px solid var(--neutral-200)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem"
                }}>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{approval.customerName}</div>
                    <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                      Quote: {approval.quoteNo} | Adjustment: {approval.adjustmentAmount >= 0 ? "+" : ""}{formatINR(approval.adjustmentAmount)}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>
                    Waiting for admin approval...
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="elegant-container fade-in-up">
        <div className="elegant-card">
          <div className="header-actions-mobile" style={{ marginBottom: "2rem" }}>
            <div>
              <h1 className="elegant-title" style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>Your Quotations</h1>
              <p className="elegant-subtitle" style={{ marginBottom: 0 }}>
                {approvedQuotes.length} {approvedQuotes.length === 1 ? "quotation" : "quotations"} created
              </p>
            </div>
            <button 
              type="button" 
              onClick={() => history.push("/q/new")}
              className="btn-elegant btn-elegant-accent"
              style={{ minHeight: "48px", width: "100%" }}
            >
              + New Quotation
            </button>
          </div>
          
          {approvedQuotes.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", position: "relative" }}>
              <div style={{ 
                marginBottom: "1rem",
                display: "flex",
                justifyContent: "center"
              }}>
                <FiFileText style={{ 
                  fontSize: "4rem", 
                  color: "var(--accent-purple)",
                  filter: "drop-shadow(0 2px 8px rgba(139, 92, 246, 0.3))"
                }} />
              </div>
              <h3 style={{ 
                color: "var(--neutral-700)", 
                marginBottom: "0.5rem",
                fontWeight: "600"
              }}>No quotations yet</h3>
              <p style={{ color: "var(--neutral-500)", marginBottom: "2rem" }}>
                Create your first quotation to get started
              </p>
              <button 
                onClick={() => history.push("/q/new")}
                className="btn-elegant btn-elegant-accent"
                style={{ minHeight: "52px", minWidth: "200px", width: "100%", maxWidth: "300px" }}
              >
                Create First Quotation
              </button>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table-elegant">
                <thead>
                  <tr>
                    <th align="left">Quote No</th>
                    <th align="left">Customer</th>
                    <th align="left">Phone</th>
                    <th align="left">Capacity</th>
                    <th align="left">Date</th>
                    <th align="left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedQuotes.map((q) => (
                    <tr key={q.meta.id}>
                      <td data-label="Quote No">
                        <span className="badge-elegant badge-elegant-primary">{q.meta.quoteNo}</span>
                      </td>
                      <td data-label="Customer" style={{ fontWeight: "600" }}>{q.customer.name}</td>
                      <td data-label="Phone" style={{ whiteSpace: "nowrap" }}>
                        {(() => {
                          const phoneData = formatPhoneNumber(q.customer.phone);
                          if (phoneData.tel) {
                            return (
                              <a
                                href={`tel:${phoneData.tel}`}
                                style={{
                                  color: "var(--accent-cyan)",
                                  textDecoration: "none",
                                  fontWeight: "500",
                                  whiteSpace: "nowrap",
                                  display: "inline-block"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.textDecoration = "underline";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.textDecoration = "none";
                                }}
                              >
                                {phoneData.display}
                              </a>
                            );
                          }
                          return <span style={{ color: "var(--neutral-400)" }}>{phoneData.display}</span>;
                        })()}
                      </td>
                      <td data-label="Capacity">
                        <span className="badge-elegant badge-elegant-gold">
                          {q.derived.capacityKw} kW
                        </span>
                      </td>
                      <td data-label="Date">{(() => {
                        const date = new Date(q.meta.createdAt);
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = date.toLocaleDateString("en-GB", { month: "long" });
                        const year = date.getFullYear();
                        const hours = date.getHours().toString().padStart(2, '0');
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        return `${day} ${month} ${year}, ${hours}:${minutes}`;
                      })()}</td>
                      <td data-label="Actions">
                        <div style={{ 
                          display: "flex", 
                          gap: "0.5rem", 
                          flexWrap: "wrap"
                        }} className="quote-actions-container">
                          <button 
                            type="button" 
                            onClick={() => openQuotePdf(q)}
                            className="btn-elegant btn-elegant-accent"
                            style={{ 
                              padding: "0.5rem 1rem", 
                              fontSize: "0.875rem", 
                              minHeight: "44px",
                              flex: "1 1 auto",
                              minWidth: "100px"
                            }}
                            title="Open PDF"
                          >
                            <FiFileText style={{ marginRight: "0.25rem" }} />
                            PDF
                          </button>
                          <button 
                            type="button" 
                            onClick={() => handlePriceAdjustment(q)}
                            className="btn-elegant"
                            style={{ 
                              padding: "0.5rem 1rem", 
                              fontSize: "0.875rem", 
                              minHeight: "44px",
                              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                              color: "white",
                              border: "none",
                              flex: "1 1 auto",
                              minWidth: "100px"
                            }}
                            title="Adjust Price"
                          >
                            <FiDollarSign style={{ marginRight: "0.25rem" }} />
                            Adjust
                          </button>
                          <button 
                            type="button" 
                            onClick={() => handleGeneratePaymentLink(q)}
                            className="btn-elegant"
                            style={{ 
                              padding: "0.5rem 1rem", 
                              fontSize: "0.875rem", 
                              minHeight: "44px",
                              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              color: "white",
                              border: "none",
                              flex: "1 1 auto",
                              minWidth: "100px"
                            }}
                            title="Generate Payment Link"
                          >
                            <FiLink style={{ marginRight: "0.25rem" }} />
                            Payment
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Price Adjustment Modal */}
      {priceAdjustmentModal.open && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "1rem",
            overflowY: "auto"
          }} onClick={() => !processing && setPriceAdjustmentModal({ open: false, quote: null })}>
            <div style={{
              background: "white",
              borderRadius: "12px",
              padding: "1.5rem",
              maxWidth: "500px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              margin: "auto"
            }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: "1.5rem", fontSize: "1.5rem", fontWeight: "600" }}>
              Adjust Price
            </h2>
            {priceAdjustmentModal.quote && (
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                  Customer: <strong>{priceAdjustmentModal.quote.customer.name}</strong>
                </div>
                <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                  Quote: <strong>{priceAdjustmentModal.quote.meta.quoteNo}</strong>
                </div>
                <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                  Current Base Price: <strong>{formatINR(priceAdjustmentModal.quote.derived.basePrice)}</strong>
                </div>
              </div>
            )}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500" }}>
                Adjustment Type
              </label>
              <div style={{ display: "flex", gap: "1rem" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="radio"
                    value="add"
                    checked={adjustmentType === "add"}
                    onChange={(e) => setAdjustmentType(e.target.value)}
                    disabled={processing}
                  />
                  Add Amount
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="radio"
                    value="subtract"
                    checked={adjustmentType === "subtract"}
                    onChange={(e) => setAdjustmentType(e.target.value)}
                    disabled={processing}
                  />
                  Subtract Amount
                </label>
              </div>
            </div>
            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500" }}>
                Amount (₹)
              </label>
              <input
                type="number"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder="Enter amount"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  border: "2px solid var(--neutral-300)",
                  fontSize: "1rem"
                }}
                disabled={processing}
                min="0"
                step="0.01"
              />
            </div>
            {profile?.role === "sales" && (
              <div style={{
                padding: "1rem",
                background: "rgba(251, 191, 36, 0.1)",
                borderRadius: "8px",
                marginBottom: "1.5rem",
                fontSize: "0.875rem",
                color: "var(--neutral-700)"
              }}>
                <strong>Note:</strong> This adjustment will require admin approval before being applied.
              </div>
            )}
            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => setPriceAdjustmentModal({ open: false, quote: null })}
                disabled={processing}
                className="btn-elegant btn-elegant-secondary"
                style={{ padding: "0.75rem 1.5rem" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPriceAdjustment}
                disabled={processing || !adjustmentAmount}
                className="btn-elegant btn-elegant-accent"
                style={{ padding: "0.75rem 1.5rem" }}
              >
                {processing ? "Processing..." : profile?.role === "admin" ? "Apply" : "Submit for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Link Modal */}
      {paymentLinkModal.open && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "1rem",
            overflowY: "auto"
          }} onClick={() => !processing && !generatedLink && setPaymentLinkModal({ open: false, quote: null })}>
            <div style={{
              background: "white",
              borderRadius: "12px",
              padding: "1.5rem",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              margin: "auto"
            }} onClick={(e) => e.stopPropagation()}>
            {!generatedLink ? (
              <>
                <h2 style={{ marginBottom: "1.5rem", fontSize: "1.5rem", fontWeight: "600" }}>
                  Generate Payment Link
                </h2>
                {paymentLinkModal.quote && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                      Customer: <strong>{paymentLinkModal.quote.customer.name}</strong>
                    </div>
                    <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                      Quote: <strong>{paymentLinkModal.quote.meta.quoteNo}</strong>
                    </div>
                    <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                      System: <strong>{paymentLinkModal.quote.derived.capacityKw}kW</strong>
                    </div>
                    <div style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                      Amount: <strong>{formatINR(getPaymentLinkAmount(paymentLinkModal.quote.derived))}</strong>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", marginBottom: "0.5rem" }}>
                    <input
                      type="checkbox"
                      checked={allowPartialPayment}
                      onChange={(e) => {
                        setAllowPartialPayment(e.target.checked);
                        if (!e.target.checked) setMinimumPartialAmount("");
                      }}
                      disabled={processing}
                    />
                    <span style={{ fontWeight: "500" }}>Allow Partial Payment</span>
                  </label>
                </div>
                {allowPartialPayment && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500" }}>
                      Minimum Partial Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={minimumPartialAmount}
                      onChange={(e) => setMinimumPartialAmount(e.target.value)}
                      placeholder="Enter minimum partial amount"
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        borderRadius: "8px",
                        border: "2px solid var(--neutral-300)",
                        fontSize: "1rem"
                      }}
                      disabled={processing}
                      min="1"
                      step="0.01"
                    />
                    <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", marginTop: "0.25rem" }}>
                      Maximum: {formatINR(getPaymentLinkAmount(paymentLinkModal.quote?.derived || {}))}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", marginBottom: "0.5rem" }}>
                    <input
                      type="radio"
                      name="expiryOption"
                      checked={useDefaultExpiry}
                      onChange={(e) => {
                        setUseDefaultExpiry(true);
                        setLinkExpiryDate("");
                        setLinkExpiryTime("");
                      }}
                      disabled={processing}
                    />
                    <span style={{ fontWeight: "500" }}>Default expiry</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", marginTop: "0.5rem" }}>
                    <input
                      type="radio"
                      name="expiryOption"
                      checked={!useDefaultExpiry}
                      onChange={(e) => setUseDefaultExpiry(false)}
                      disabled={processing}
                    />
                    <span style={{ fontWeight: "500" }}>Set expiry</span>
                  </label>
                </div>
                {!useDefaultExpiry && (
                  <div style={{ marginBottom: "1.5rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500" }}>
                        Date
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="date"
                          value={linkExpiryDate}
                          onChange={(e) => setLinkExpiryDate(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            paddingRight: "2.5rem",
                            borderRadius: "8px",
                            border: "2px solid var(--neutral-300)",
                            fontSize: "1rem"
                          }}
                          disabled={processing}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <span style={{
                          position: "absolute",
                          right: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--accent-purple)",
                          pointerEvents: "none"
                        }}>📅</span>
                      </div>
                    </div>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500" }}>
                        Time
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="time"
                          value={linkExpiryTime}
                          onChange={(e) => setLinkExpiryTime(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            paddingRight: "2.5rem",
                            borderRadius: "8px",
                            border: "2px solid var(--neutral-300)",
                            fontSize: "1rem"
                          }}
                          disabled={processing}
                        />
                        <span style={{
                          position: "absolute",
                          right: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--accent-purple)",
                          pointerEvents: "none"
                        }}>🕐</span>
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setPaymentLinkModal({ open: false, quote: null })}
                    disabled={processing}
                    className="btn-elegant btn-elegant-secondary"
                    style={{ padding: "0.75rem 1.5rem" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePaymentLink}
                    disabled={processing || (allowPartialPayment && !minimumPartialAmount) || (!useDefaultExpiry && (!linkExpiryDate || !linkExpiryTime))}
                    className="btn-elegant btn-elegant-accent"
                    style={{ padding: "0.75rem 1.5rem" }}
                  >
                    {processing ? "Creating..." : "Create Link"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ marginBottom: "1.5rem", fontSize: "1.5rem", fontWeight: "600", color: "var(--success)" }}>
                  Payment Link Created Successfully!
                </h2>
                <div style={{ marginBottom: "1.5rem" }}>
                  <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500" }}>
                    Payment Link
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      style={{
                        flex: 1,
                        padding: "0.75rem",
                        borderRadius: "8px",
                        border: "2px solid var(--neutral-300)",
                        fontSize: "0.875rem",
                        background: "var(--neutral-50)"
                      }}
                    />
                    <button
                      onClick={() => copyToClipboard(generatedLink)}
                      className="btn-elegant btn-elegant-accent"
                      style={{ padding: "0.75rem 1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}
                    >
                      {linkCopied ? <FiCheck /> : <FiCopy />}
                      {linkCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => {
                      setPaymentLinkModal({ open: false, quote: null });
                      setGeneratedLink(null);
                    }}
                    className="btn-elegant btn-elegant-accent"
                    style={{ padding: "0.75rem 1.5rem" }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


